import {useAlert} from "@contexts/hooks/useAlert.ts";
import {useGeneral} from "@contexts/GeneralContext.tsx";
import {type ISolicitacaoOrcamento} from "@models/solicitacoes/orcamento.js";
import type {IRecurso} from "@models/recurso/IRecurso.ts";
import {Grid, TableCell, TableRow, Typography} from "@mui/material";
import {ToDateDefault, ToMoney} from "@shared/MathHelp.js";
import React, {useEffect, useState} from "react";
import {DefaultInfoV1Props, DefaultNavLinkProps} from "@models/DefaultProps.ts";
import {NavigationLink} from "@shared/custom/NavigationLink.tsx";
import {RecursoInformacao} from "@components/recurso/RecursoInformacao";
import {
    SolicitacaoItemInformacao,
    SolicitacaoItemInformacaoNavLink,
} from "@components/solicitacoes/itens/SolicitacaoItemInformacao.tsx";
import {CurrencyExchange, LocalShipping} from "@mui/icons-material";
import {useContextMenu} from "@contexts/hooks/useContextMenu.ts";
import {OrcamentoFlagsPage} from "@components/orcamento/pages/OrcamentoFlagsPage.tsx";
import {RastreamentosPage} from "@components/orcamento/rastreio/pages/RastreamentosPage.tsx";
import {TransferenciaRecursoSolicitacao} from "@components/recurso/transferencias/TransferenciaRecursoSolicitacao.tsx";
import {statusOrcamentoColor} from "@models/solicitacoes/statusOrcamentoColor.ts";
import {FornecedorInfoNavLink} from "@components/fornecedores/FornecedorInformacao.tsx";

interface OrcamentoInfoProps extends DefaultInfoV1Props<ISolicitacaoOrcamento> {
    EndComponent?: (item: ISolicitacaoOrcamento) => React.ReactNode;
    variant: 'navigationLink' | 'row' | 'list';
    slotProps?: {
        solicitacaoItem?: Partial<React.ComponentProps<typeof SolicitacaoItemInformacaoNavLink>>
    }
}

export function OrcamentoInfo(props: OrcamentoInfoProps) {
    const {variant} = props;

    if (variant == 'list') return <OrcamentoInfoList {...props}/>;
    if (variant == 'navigationLink') return <OrcamentoInfoNavLink {...props}/>;
    if (variant == 'row') return <OrcamentoInfoRow {...props}/>;
}

export function OrcamentoInfoRow(props: Omit<OrcamentoInfoProps, 'variant' | 'overrides'>) {
    const {EndComponent} = props;

    const [value, setValue] = useState<ISolicitacaoOrcamento | undefined>(props.value)

    useEffect(() => {
        if (value != null) {
            setValue(props.value)
        }
    }, [props.value])

    return (value &&
        <TableRow>
            <TableCell>
                <OrcamentoInfoNavLink slotProps={{chip: {tableMode: true}}} {...props}/>
            </TableCell>
            <TableCell>
                {value.solicitacaoItem &&
                    <SolicitacaoItemInformacaoNavLink slotProps={{chip: {tableMode: true}}}
                                                      hash={'Orcamento'}
                                                      searchData={{
                                                          solicitacaoItemId: value.solicitacaoItem.id,
                                                          orcamentoId: value.id,
                                                          produtoId: value.solicitacaoItem.produtoId,
                                                          solicitacaoId: value.solicitacaoId,
                                                          formaPagamentoId: value.formaPagamentoId
                                                      }}
                                                      value={value.solicitacaoItem}
                                                      {...props.slotProps?.solicitacaoItem}/>}
            </TableCell>
            <TableCell>
                {value.fornecedor != null &&
                    <FornecedorInfoNavLink ignoreNavigate
                                           value={value.fornecedor}/>}
            </TableCell>
            <TableCell>
                <Typography>{ToMoney(value.valorTotal)}</Typography>
            </TableCell>
            <TableCell>
                <Typography>{value.qtdAtendida}/{value.solicitacaoItem?.quantidade}</Typography>
            </TableCell>
            <TableCell>
                <Typography color={statusOrcamentoColor(value.status)}>{value.status}</Typography>
            </TableCell>
            <TableCell>
                <Typography>{ToDateDefault(value.dataOrcamento)}</Typography>
            </TableCell>
            <TableCell align={'right'}>
                {EndComponent && <EndComponent {...value}/>}
            </TableCell>
        </TableRow>
    );
}


export function OrcamentoInfoNavLink(props: Omit<OrcamentoInfoProps, 'EndComponent' | 'variant' | 'overrides'> & DefaultNavLinkProps<ISolicitacaoOrcamento>) {
    const {setContextOptions} = useContextMenu()
    const {addPopUp} = useAlert()

    const [value, setValue] = useState<ISolicitacaoOrcamento | undefined>(props.value)

    useEffect(() => {
        if (value != null) {
            setValue(props.value)
        }
    }, [props.value])


    const handleCtx = (e: React.MouseEvent<HTMLElement>) => {
        if (value == null) return;
        setContextOptions({
            anchor: e.currentTarget, event: e, options: [{
                label: 'Visualizar sinalizações', onClick: () => {
                    addPopUp({
                        title: `Sinalizações orçamento ${value.id} ${ToMoney(value.valorTotal)}`,
                        content: <OrcamentoFlagsPage
                            filtros={{orcamentoId: props.value?.id}}></OrcamentoFlagsPage>,
                        size: 'md'
                    })
                }
            },
                {
                    label: 'Codigos de rastreio',
                    endIcon: <LocalShipping/>,
                    onClick: () => {
                        addPopUp({
                            title: `Codigos de rastreio orçamento ${value.id} ${ToMoney(value.valorTotal)}`,
                            content: <RastreamentosPage hideHeader filtros={{idOrcamento: [value.id]}}/>,
                            size: 'xl'
                        })
                    }
                },
                {
                    label: 'Visualizar solicitacao de transferencia',
                    endIcon: <CurrencyExchange/>,
                    disabled: props.value?.transferencia == null,
                    onClick: () => {
                        if (value.transferencia == null) return;
                        addPopUp({
                            title: `Solicitacao de transferencia orçamento ${value.id} ${ToMoney(value.valorTotal)}`,
                            content: <TransferenciaRecursoSolicitacao transferencia={value.transferencia}/>,
                            size: 'xl'
                        })
                    }
                }
            ]
        })
    }


    return (value && <NavigationLink label={value.id.toString()}
                                     pathName={'/orcamento/detalhes'}
                                     onContextMenu={handleCtx}
                                     toolTipHint={"Ir para a pagina de detalhes do orçamento " + value.id + " " + ToMoney(value.valorTotal)}
                                     tableMode={props.slotProps?.chip?.tableMode}
                                     chipSx={{...props.slotProps?.chip?.sx}}
                                     onClick={props.onClick}
                                     searchData={{
                                         orcamentoId: value.id.toString(),
                                     }}/>);
}

export function OrcamentoInfoList(props: OrcamentoInfoProps) {
    const {EndComponent} = props;
    const {screenSize} = useGeneral();
    const [recurso, setrecurso] = useState<IRecurso | null>(null);

    const [value, setValue] = useState<ISolicitacaoOrcamento | undefined>(props.value)

    useEffect(() => {
        if (value != null) {
            setValue(props.value)
        }
    }, [props.value])


    useEffect(() => {
            if (value?.solicitacao?.recurso)
                setrecurso(value.solicitacao.recurso);
        },
        [value]);


    return (value &&
        <Grid size={12}>
            <Grid container spacing={1} justifyContent={'space-between'}>
                <Grid alignContent={'center'}>
                    <Grid
                        container
                        spacing={1}
                        sx={{
                            display: 'flex',
                            flexWrap: 'nowrap',
                            overflowX: 'auto',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            width: '100%',
                            alignItems: 'center',
                        }}
                        justifyContent={'space-between'}
                    >
                        <Grid>
                            <OrcamentoInfoNavLink {...props}/></Grid>
                        {value.solicitacaoItem?.produto && (
                            <Grid>
                                <SolicitacaoItemInformacao value={value.solicitacaoItem} variant={'navigationLink'}/>
                            </Grid>
                        )}
                        {value.fornecedor != null &&
                            <FornecedorInfoNavLink slotProps={{chip: {tableMode: true}}}
                                                   value={value.fornecedor}/>}
                        {screenSize != 'micro' && (<>
                            <Grid>
                                <Typography>{ToMoney(value.valorTotal)}</Typography>
                            </Grid>
                            <Grid>
                                <Typography>{value.qtdAtendida}</Typography>
                            </Grid>
                            {recurso && <Grid>
                                <RecursoInformacao value={recurso} variant={'navigationLink'}/>
                            </Grid>}
                        </>)}
                        <Grid>
                            <Typography color={statusOrcamentoColor(value.status)}>
                                {value.status}
                            </Typography>
                        </Grid>
                        <Grid>
                            <Typography>{ToDateDefault(value.dataOrcamento)}</Typography>
                        </Grid>
                    </Grid>
                </Grid>
                {
                    EndComponent && <Grid><EndComponent {...value}/></Grid>
                }
            </Grid>
        </Grid>
    );
}